/**
 * The four error codes only the edge proxy can produce.
 *
 * They come from error.schema.json's `x-edgeOnly` block rather than a list
 * typed here, because the whole point of the block is that the origin cannot
 * report that it was unreachable — a fifth code added to the contract has to
 * reach this surface without anyone remembering to copy it across.
 *
 * flows.json `globalRules.originUnreachable` is what this module exists to
 * serve: these four never enter a screen's own `error` state, because that
 * copy blames the screen ("Couldn't load transactions") when the truth is that
 * nothing at all is reachable. They present `serviceUnavailable` instead.
 */
import errorSchema from '@/generated/product/data/error.schema.json'
// `import type` only: api.ts imports this module to classify a failure it is in
// the middle of constructing, so a runtime edge -> api edge would be a cycle.
import type { ApiFailure } from './api'

/** Keys of `x-edgeOnly` minus its `$comment`, which is prose about the block. */
export const EDGE_CODES: readonly string[] = Object.keys(errorSchema.properties.code['x-edgeOnly']).filter(
  (key) => !key.startsWith('$'),
)

export function isEdgeCode(code: string): boolean {
  return EDGE_CODES.includes(code)
}

export function isEdgeFailure(error: unknown): boolean {
  const code = (error as ApiFailure | null | undefined)?.body?.code
  return typeof code === 'string' && isEdgeCode(code)
}

/**
 * Whether the user may press Retry on this failure.
 *
 * `retryable` means something sharper for the edge codes than "will it work
 * this time": it is whether the request is KNOWN not to have reached the mini.
 * `tunnelUnavailable` and `tunnelBusy` were refused before handoff and are
 * always safe. `originUnreachable` and `originTimeout` were handed over and may
 * already have been applied — re-issuing a statement upload there imports it
 * twice, so the relay sets `retryable: false` and no button is offered.
 * See globalRules.noAutoRetry.
 */
export function isRetryable(failure: ApiFailure): boolean {
  return failure.body.retryable !== false
}
